import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { useQuery } from "react-query";
import { useStateMachine } from "little-state-machine";
import { useCurrencyState } from "@hooks/useCurrency";
import { getInitialVariant } from "../lib/variant-utils";
import { updateAction } from "../updateAction";
import { getMediaListFromProduct } from "../../lib/get-media";
import { parseURLParams } from "../../lib/url-utils";
import { fetchAllVariants } from "../../lib/fetch-variants";
import { mergeShopifyAndSanityProducts } from "../../lib/merge-shopify-sanity-products";

export function useVariants(sanityProductGroup) {
  const { currency } = useCurrencyState();
  const initialAvailableProducts = sanityProductGroup?.shopifyProducts?.reduce((acc, shopifyProduct) => {
    if (
      shopifyProduct &&
      shopifyProduct.productReferenceV2 &&
      shopifyProduct.productReferenceV2.store.status === "active"
    ) {
      acc.push(shopifyProduct.productReferenceV2.store.slug.current);
    }
    return acc;
  }, []) || [];
  
  const [availableProducts, setAvailableProducts] = useState(initialAvailableProducts);
  const [productGroup, setProductGroup] = useState({});
  const [images, setImages] = useState([]);
  const [variants, setVariants] = useState([]);
  const router = useRouter();
  const {
    _,
    isError,
    error,
    isLoading,
  } = useQuery(
    ["variants", sanityProductGroup?.key, availableProducts],
    async () => {
      console.log("Fetching variants for products:", availableProducts);
      try {
        const result = await fetchAllVariants(availableProducts, currency);
        const filteredResult = result.filter(item => item !== null);
        setVariants(filteredResult);
        return filteredResult;
      } catch (err) {
        console.error("Error in fetchAllVariants: ", err);
        throw err;
      }
    },
    {
      enabled: availableProducts.length > 0,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      staleTime: 0,
      onSuccess: (data) => {
        console.log("useQuery success, variants set to:", data);
      },
      onError: (err) => {
        console.error("useQuery error:", err);
      }
    }
  );
  const { actions, state } = useStateMachine({ updateAction });

  useEffect(() => {
    const newAvailableProducts = sanityProductGroup?.shopifyProducts?.reduce((acc, shopifyProduct) => {
      if (
        shopifyProduct &&
        shopifyProduct.productReferenceV2 &&
        shopifyProduct.productReferenceV2.store.status === "active"
      ) {
        acc.push(shopifyProduct.productReferenceV2.store.slug.current);
      }
      return acc;
    }, []) || [];
    
    setAvailableProducts(newAvailableProducts);
  }, [sanityProductGroup]);

  useEffect(() => {
    if (variants && variants.length) {
      const productGroup = mergeShopifyAndSanityProducts(sanityProductGroup, variants);
      const { query, selectedProduct } = parseURLParams(productGroup, variants, router);
      router.replace(
        {
          pathname: "/products/[slug]",
          query,
        },
        undefined,
        { shallow: true }
      );

      setProductGroup(productGroup);
      const updatedState = {};
      if (selectedProduct) {
        updatedState[`product`] = selectedProduct;
        updatedState[`variant`] = getInitialVariant(
          selectedProduct.productReferenceV2.variants,
          router
        );
      }
      updatedState[`productGroup`] = productGroup;
      actions.updateAction(updatedState);
    }
  }, [variants]);

  useEffect(() => {
    setImages(getMediaListFromProduct(productGroup, state[`product`]));
  }, [productGroup, state]);

  // Add a debug effect to monitor variants
  useEffect(() => {
    console.log("variants value changed:", variants);
  }, [variants]);

  return { isError, error, isLoading, images };
}